import { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { isEqual } from 'lodash';
import Yup from '../../../utils/yup';

import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import useMenus from '../../../context/MenuContext';
import useUsers from '../../../context/UsersContext';
import { FormButtonPanelUI, FormHeaderUI, FormBase, DeleteDialogUI, ButtonUI } from '../../Interface';
import { cleanValues, generateAutocompleteItems } from '../../../utils/helper';
import useConfig from '../../../context/ConfigContext';
import masterDataService from '../../../service/masterData.service';
import ServiceOperationTabs from './ComplaintReportTabs';
import { useLoader } from '../../../provider/LoaderProvider';
import ComplaintReports from './ComplaintReports';
import ComplaintReporFields from './ComplaintReporFields';
import NoteDialog from './NoteDialog';
import dayjs from 'dayjs';
import { ReportType } from '../../../config/complaintReport';
import complaintReportService from '../../../service/NewComplaint/complaintReport.service';

export const ComplaintReportPage = ({ type }) => {
  const { loading, showLoader, hideLoader } = useLoader();
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const { getUser, user } = useUsers();
  const { getConfig, config } = useConfig();
  const [refetchTable, setRefetchTable] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [canEdit, setCanEdit] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [defaultValues, setDefaultValues] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [initValues, setInitValues] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({
    employee: [],
    complaints: [],
    company: [],
  });
  const [autocompleteData, setAutoCompleteData] = useState({});

  const defNoteDialogState = {
    open: false,
    fieldName: '',
  };
  const [noteDialogState, setNoteDialogState] = useState(defNoteDialogState);

  const defTeam = {
    id: null,
    isNew: true,
    resourceOneId: null,
    position: '',
    specialization: '',
  };

  const defAction = {
    id: null,
    isNew: true,
    name: '',
    responsiblePerson: '',
    date: null,
    status: '',
  };

  const defPermanentAction = {
    id: null,
    isNew: true,
    name: '',
    responsiblePerson: '',
    date: null,
    status: '',
  };
  const defSolution = {
    id: null,
    isNew: true,
    name: '',
    responsiblePerson: '',
    date: null,
    status: '',
  };

  const def = {
    type,
    supplier: '',
    supplierContactPersonId: null,
    supplierEmail: '',
    supplierPhoneNumber: '',
    supplierComplaintId: null,
    itemCode: '',
    supplierComplaintDate: null,
    companyId: null,
    customerContactPerson: '',
    customerEmail: '',
    customerPhoneNumber: '',
    customerComplaintId: '',
    customerComplaintDate: null,
    other: '',
    issueDescription: '',
    rejectedQty: '',
    blockedQty: '',
    productionDate: null,
    returnedQty: '',
    why1: '',
    why2: '',
    why3: '',
    why4: '',
    why5: '',
    why1Answer: '',
    why2Answer: '',
    why3Answer: '',
    why4Answer: '',
    why5Answer: '',
    description: '',
    projectCompleted: '',
    resourceOneId: null,
    projectStatusDate: dayjs(),
    team: [],
    action: [],
    permanentAction: [],
    solution: [],
  };

  useEffect(() => {
    getMenus();
    getConfig();
    if (!Object.keys(user).length) getUser();

    setDefaultValues(def);
    setDefaultSelectedValues(def);
    initDefaultValues();
  }, []);

  useEffect(() => {
    setOperatorsDisabled(true);
    setSelectedValues({});
    setViewField(true);
  }, [type]);

  useEffect(() => {
    const key = type === ReportType.REPORT4D ? 'complaintReport4D' : 'complaintReport8D';

    const submenuId = getMenuSubMenuId(key);
    setCanEdit(getCanEdit(submenuId));
  }, [menus, type]);

  useEffect(() => {
    if (!loading && Object.keys(config).length > 0) {
      setDefaultValues((prev) => {
        return {
          ...prev,
          supplier: config?.name ?? null,
        };
      });
    }
  }, [config, loading]);

  useEffect(() => {
    if (!loading && Object.keys(user).length > 0) {
      const employee = autocompleteData?.employee?.find((item) => item.id === user.employeeId);

      if (employee) {
        setDefaultValues((prev) => {
          return {
            ...prev,
            supplierContactPersonId: employee.id,
            supplierEmail: employee.email,
            supplierPhoneNumber: employee.phoneNumber,
            resourceOneId: employee.id,
          };
        });
      }
    }
  }, [user.employeeId, loading]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  const validationSchema = Yup.object().shape({
    supplier: Yup.string().nullable(),
    supplierContactPersonId: Yup.string().nullable(),
    supplierEmail: Yup.string().nullable(),
    supplierPhoneNumber: Yup.string().nullable(),
    supplierComplaintId: Yup.string().nullable(),
    itemCode: Yup.string().nullable(),
    supplierComplaintDate: Yup.string().nullable(),
    companyId: Yup.string().nullable(),
    customerContactPerson: Yup.string().nullable(),
    customerEmail: Yup.string().nullable(),
    customerPhoneNumber: Yup.string().nullable(),
    customerComplaintId: Yup.string().nullable(),
    customerComplaintDate: Yup.string().nullable(),
    other: Yup.string().nullable(),
    issueDescription: Yup.string().nullable(),
    rejectedQty: Yup.string().nullable(),
    blockedQty: Yup.string().nullable(),
    productionDate: Yup.string().nullable(),
    returnedQty: Yup.string().nullable(),
    why1: Yup.string().nullable(),
    why2: Yup.string().nullable(),
    why3: Yup.string().nullable(),
    why4: Yup.string().nullable(),
    why5: Yup.string().nullable(),
    why1Answer: Yup.string().nullable(),
    why2Answer: Yup.string().nullable(),
    why3Answer: Yup.string().nullable(),
    why4Answer: Yup.string().nullable(),
    why5Answer: Yup.string().nullable(),
    description: Yup.string().nullable(),
    projectCompleted: Yup.string().nullable(),
    resourceOneId: Yup.string().nullable(),
    projectStatusDate: Yup.string().nullable(),
    team: Yup.array().of(
      Yup.object().shape({
        resourceOneId: Yup.string().nullable(),
        position: Yup.string().nullable(),
        specialization: Yup.string().nullable(),
      })
    ),
    action: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().nullable(),
        responsiblePerson: Yup.string().nullable(),
        date: Yup.string().nullable(),
        status: Yup.string().nullable(),
      })
    ),
    permanentAction: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().nullable(),
        responsiblePerson: Yup.string().nullable(),
        date: Yup.string().nullable(),
        status: Yup.string().nullable(),
      })
    ),
    solution: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().nullable(),
        responsiblePerson: Yup.string().nullable(),
        date: Yup.string().nullable(),
        status: Yup.string().nullable(),
      })
    ),
  });

  const mapComplaintOptions = (complaints) =>
    complaints.map((c) => {
      const dateFormatted = dayjs(c.date).format('YYYY-MM-DD');

      return {
        value: c.id,
        label: c.reference,
        fullLabel: `${c.company.name} - ${c.reference} - ${dateFormatted}`,
      };
    });

  const initDefaultValues = () => {
    showLoader();
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .employeesToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .companyToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .complaintToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          employee: resp[0],
          company: resp[1],
          complaints: resp[2],
        };

        setAutoCompleteData(aData);

        const filteredCompany = aData.company?.filter((company) => company.customer);

        const acOptions = {
          employee: generateAutocompleteItems(aData.employee, 'name', 'id'),
          company: generateAutocompleteItems(filteredCompany, 'name', 'id'),
          complaints: mapComplaintOptions(aData.complaints),
        };

        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const valuesToForm = () => {
    const {
      id,
      supplier,
      supplierContactPersonId,
      supplierEmail,
      supplierPhoneNumber,
      supplierComplaintId,
      itemCode,
      supplierComplaintDate,
      companyId,
      customerContactPerson,
      customerEmail,
      customerPhoneNumber,
      customerComplaintId,
      customerComplaintDate,
      other,
      issueDescription,
      rejectedQty,
      blockedQty,
      productionDate,
      returnedQty,
      why1,
      why2,
      why3,
      why4,
      why5,
      why1Answer,
      why2Answer,
      why3Answer,
      why4Answer,
      why5Answer,
      description,
      projectCompleted,
      resourceOneId,
      projectStatusDate,
      complaintReportTeam,
      complaintReportAction,
      complaintReportPermanentAction,
      complaintReportSolution,
    } = selectedValues;

    setInitValues({
      id,
      supplier: supplier ?? defaultValues.supplier,
      supplierContactPersonId: supplierContactPersonId ?? defaultValues.supplierContactPersonId,
      supplierEmail: supplierEmail ?? defaultValues.supplierEmail,
      supplierPhoneNumber: supplierPhoneNumber ?? defaultValues.supplierPhoneNumber,
      supplierComplaintId: supplierComplaintId ?? defaultValues.supplierComplaintId,
      itemCode: itemCode ?? defaultValues.itemCode,
      supplierComplaintDate: dayjs(supplierComplaintDate),
      companyId: companyId ?? defaultValues.companyId,
      customerContactPerson: customerContactPerson ?? defaultValues.customerContactPerson,
      customerEmail: customerEmail ?? defaultValues.customerEmail,
      customerPhoneNumber: customerPhoneNumber ?? defaultValues.customerPhoneNumber,
      customerComplaintId: customerComplaintId ?? defaultValues.customerComplaintId,
      customerComplaintDate: dayjs(customerComplaintDate),
      other: other ?? defaultValues.other,
      issueDescription: issueDescription ?? defaultValues.issueDescription,
      rejectedQty: rejectedQty ?? defaultValues.rejectedQty,
      blockedQty: blockedQty ?? defaultValues.blockedQty,
      productionDate: dayjs(productionDate),
      returnedQty: returnedQty ?? defaultValues.returnedQty,
      why1: why1 ?? defaultValues.why1,
      why2: why2 ?? defaultValues.why2,
      why3: why3 ?? defaultValues.why3,
      why4: why4 ?? defaultValues.why4,
      why5: why5 ?? defaultValues.why5,
      why1Answer: why1Answer ?? defaultValues.why1Answer,
      why2Answer: why2Answer ?? defaultValues.why2Answer,
      why3Answer: why3Answer ?? defaultValues.why3Answer,
      why4Answer: why4Answer ?? defaultValues.why4Answer,
      why5Answer: why5Answer ?? defaultValues.why5Answer,
      description: description ?? defaultValues.description,
      projectCompleted: projectCompleted ?? defaultValues.projectCompleted,
      resourceOneId: resourceOneId ?? defaultValues.resourceOneId,
      projectStatusDate: dayjs(projectStatusDate),
      team: complaintReportTeam.map((tm) => ({
        id: tm.id,
        resourceOneId: tm.resourceOneId ?? null,
        position: tm.position ?? '',
        specialization: tm.specialization ?? '',
        createdBy: tm.createdBy,
      })),
      action: complaintReportAction.map((ac) => ({
        id: ac.id,
        name: ac.name ?? '',
        responsiblePerson: ac.responsiblePerson ?? '',
        date: dayjs(ac.date),
        status: ac.status ?? '',
        createdBy: ac.createdBy,
      })),
      permanentAction: complaintReportPermanentAction.map((pac) => ({
        id: pac.id,
        name: pac.name ?? '',
        responsiblePerson: pac.responsiblePerson ?? '',
        date: dayjs(pac.date),
        status: pac.status ?? '',
        createdBy: pac.createdBy,
      })),
      solution: complaintReportSolution.map((sol) => ({
        id: sol.id,
        name: sol.name ?? '',
        responsiblePerson: sol.responsiblePerson ?? '',
        date: dayjs(sol.date),
        status: sol.status ?? '',
        createdBy: sol.createdBy,
      })),
    });
  };

  const handleSubmit = (values, { resetForm }) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values, resetForm);
    }
  };

  const updateObject = async (values) => {
    const updData = {
      ...cleanValues(values),
    };

    complaintReportService.updateItem(updData).then((data) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });

      setSelectedValues(data);
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleInsertObject = async (values) => {
    const insertObject = {
      ...cleanValues(values),
    };

    complaintReportService
      .createItem(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });

        setSelectedValues(data);
        setRowSelectionModel(data.id);
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
      })
      .finally(() => {
        //@TODO loader
      });
  };

  const handleRemoveElement = async () => {
    complaintReportService.deleteItem(selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues({});
      setInitValues(def);
    });
  };

  const printClick = () => {
    addNotification({
      content: 'Nyomtatás elindult!',
      type: NotificationType.INFO,
    });

    complaintReportService.printDocument({
      docId: selectedValues.id,
      type,
    });
  };

  return (
    <>
      <FormBase
        name="ComplaintReport"
        leftPanelContent={
          <>
            <div className="h-full overflow-auto custom-scrollbar">
              <div className="flex items-center flex-wrap justify-between">
                <div className="flex items-center flex-wrap">
                  <FormHeaderUI
                    addClick={() => {
                      setSelectedValues(defaultSelectedValues);
                      setViewField(false);
                      setAddField(false);
                    }}
                    addDisabled={!viewField || !canEdit}
                    modClick={() => {
                      setViewField(false);
                      setEditButtonvisible(false);
                    }}
                    deleteClick={() => {
                      setShowDeleteDialog(true);
                    }}
                    deleteDisabled={!viewField || operatorsDisabled || !canEdit}
                    modDisabled={!viewField || operatorsDisabled || !canEdit}
                    copyClick={() => {
                      setViewField(false);
                      setAddField(false);
                    }}
                    copyDisabled={!viewField || operatorsDisabled || !canEdit}
                  />
                  <ButtonUI
                    text="Nyomtatás"
                    size="sm"
                    className="bg-labelColor py-3"
                    onClick={() => printClick()}
                    disabled={!viewField || operatorsDisabled}
                  />
                </div>
              </div>

              <Formik
                initialValues={initValues}
                validationSchema={validationSchema}
                validateOnChange={false}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ resetForm }) => (
                  <Form>
                    <ComplaintReporFields
                      viewField={viewField}
                      autoCompleteOptions={autoCompleteOptions}
                      selectedValues={selectedValues}
                      autocompleteData={autocompleteData}
                    />

                    <ServiceOperationTabs
                      disabled={viewField}
                      autoCompleteOptions={autoCompleteOptions}
                      autocompleteData={autocompleteData}
                      defAction={defAction}
                      defPermanentAction={defPermanentAction}
                      defSolution={defSolution}
                      defTeam={defTeam}
                      setNoteDialogState={setNoteDialogState}
                      type={type}
                    />

                    {noteDialogState.open && noteDialogState.fieldName && (
                      <NoteDialog
                        open={noteDialogState.open}
                        noteDialogState={noteDialogState}
                        onClose={() => setNoteDialogState(defNoteDialogState)}
                      />
                    )}

                    <FormButtonPanelUI
                      onCancelClick={() => {
                        setViewField(true);
                        setEditButtonvisible(true);
                        setAddField(true);

                        resetForm();
                      }}
                      disabled={editButtonVisible && addField}
                    />
                  </Form>
                )}
              </Formik>
            </div>
          </>
        }
        rightPanelContent={
          <ComplaintReports
            refetch={{ refetchTable, setRefetchTable }}
            values={{ selectedValues, setSelectedValues }}
            viewField={viewField}
            operatorsDisabled={operatorsDisabled}
            rowSelection={{ rowSelectionModel, setRowSelectionModel }}
            autoCompleteOptions={autoCompleteOptions}
            autocompleteData={autocompleteData}
            type={type}
          />
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};
